import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * useScrollReveal
 * Unified IntersectionObserver-based reveal system.
 * Animates opacity (0 -> 1) and translateY (20px -> 0) over 800ms ease-out.
 * Triggered once when element reaches ~85% viewport height.
 * Bypassed gracefully for prefers-reduced-motion.
 */
export function useScrollReveal() {
  const location = useLocation();

  useEffect(() => {
    // If reduced motion is preferred, reveal everything immediately
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      document.querySelectorAll(".reveal-on-scroll").forEach((el) => {
        el.classList.add("is-revealed");
      });
      return;
    }

    if (!("IntersectionObserver" in window)) {
      document.querySelectorAll(".reveal-on-scroll").forEach((el) => {
        el.classList.add("is-revealed");
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: "0px 0px -12% 0px", // Trigger when ~85% of viewport height is reached
        threshold: 0.08,
      }
    );

    // Give DOM time to settle following route changes
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll(
        ".reveal-on-scroll:not(.is-revealed)"
      );

      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // If already visible near top of screen, reveal immediately without jump
        if (rect.top < window.innerHeight * 0.9) {
          el.classList.add("is-revealed");
        } else {
          observer.observe(el);
        }
      });
    }, 50);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [location.pathname]);
}

export default useScrollReveal;
